/**
 * This function is used to transform filter parameters into grouped facets.
 * It maps input options to facet labels.
 *
 * @param {Object | string} input - An object representing the filter parameters. It can also accept a string, which will be returned in a nested array.
 * @param {string} [parentPath=''] - The path of the parent property, used for nesting when transforming objects.
 * @returns {string[][]} - The resulting array of grouped facets. Each group is represented by a subarray of strings.
 */
export const filtersToFacets = (
  rawInput: { [key: string]: any } | string,
  parentPath = '',
): string[][] => {
  const input = JSON.parse(JSON.stringify(rawInput))

  const getFacetFromPath = (path: string, value: any): string => {
    return `${path}:${value}`
  }

  const grouped: { [key: string]: string[] } = {}

  if (input?.price_ratio) {
    const priceRatio = input.price_ratio
      .map((ratio: string) => ratio.split(','))
      .flat()

    grouped['price_ratio'] = priceRatio.map((ratio: string) =>
      getFacetFromPath('price_ratio', ratio),
    )

    delete input.price_ratio
  }

  if (typeof input === 'string') {
    return [[input]]
  }

  // Handle special case for band_size and cup_size
  if (input.options?.band_size && input.options?.cup_size) {
    const variants: string[] = []
    input.options.band_size.forEach((band: string) => {
      input.options.cup_size.forEach((cup: string) => {
        variants.push(getFacetFromPath('variant_title', `${band} / ${cup}`))
      })
    })

    // Combine both variant and size options into one 'options' group
    if (input.options?.size) {
      grouped['options'] = [
        ...(grouped['options'] || []),
        ...variants,
        ...input.options.size.map((size: string) =>
          getFacetFromPath('options.size', size),
        ),
      ]
      delete input.options.size
    } else {
      grouped['options'] = [...(grouped['options'] || []), ...variants]
    }

    delete input.options.band_size
    delete input.options.cup_size
  }

  for (const key in input) {
    const currentPath = parentPath ? `${parentPath}.${key}` : key
    const value = input[key]

    if (Array.isArray(value) && value.length > 0) {
      const facetItems = value.map((item) =>
        getFacetFromPath(currentPath, item),
      )
      grouped[currentPath] = [...(grouped[currentPath] || []), ...facetItems]
    } else if (typeof value === 'object' && value !== null) {
      const subGroup = filtersToFacets(value, currentPath)
      subGroup.forEach((group) => {
        group.forEach((groupItem) => {
          const [groupItemKey] = groupItem.split(':')
          grouped[groupItemKey] = grouped[groupItemKey] || []
          grouped[groupItemKey].push(groupItem)
        })
      })
    } else if (typeof value === 'string' || typeof value === 'number') {
      grouped[currentPath] = [
        ...(grouped[currentPath] || []),
        getFacetFromPath(currentPath, value),
      ]
    }
  }

  return Object.values(grouped).filter((group) => group.length > 0) // filtering out any empty groups
}
